import React from 'react';
import { NavLink, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import DashboardPage from './pages/DashboardPage';
import TipsPage from './pages/TipsPage';
import ReviewsPage from './pages/ReviewsPage';
import MorePage from './pages/MorePage';
import EmployeesPage from './pages/EmployeesPage';
import EmployeeDetailPage from './pages/EmployeeDetailPage';
import AddEmployeePage from './pages/AddEmployeePage';
import PoolDistributionPage from './pages/PoolDistributionPage';
import TransactionsPage from './pages/TransactionsPage';
import TransactionsAllPage from './pages/TransactionsAllPage';
import ReportPage from './pages/ReportPage';
import TransactionReportPage from './pages/TransactionReportPage';
import PayoutPage from './pages/PayoutPage';
import PayoutHistoryPage from './pages/PayoutHistoryPage';
import ShopInfoPage from './pages/ShopInfoPage';
import TipModePage from './pages/TipModePage';
import BankAccountsPage from './pages/BankAccountsPage';
import GstVerificationPage from './pages/GstVerificationPage';
import NotificationsPage from './pages/NotificationsPage';
import ChangePasswordPage from './pages/ChangePasswordPage';
import ContactUsPage from './pages/ContactUsPage';
import LoginPage from './pages/LoginPage';
import { webApi } from './services/api';
import {
  ANDROID_IC_REPORT_FILLED,
  ANDROID_IC_REPORT_OUTLINE,
  ANDROID_IC_TIPS_FILLED,
  ANDROID_IC_TIPS_OUTLINE
} from './icons/androidEmployerNavPaths';

const navItems = [
  { to: '/dashboard', label: 'Home', icon: 'home' },
  { to: '/transactions', label: 'Transactions', icon: 'transactions' },
  { to: null, label: '', isPlaceholder: true },
  { to: '/report', label: 'Analytics', icon: 'analytics' },
  { to: '/more', label: 'Settings', icon: 'more' }
];

function QrIcon() {
  return (
    <svg width="26" height="26" viewBox="0 0 24 24" aria-hidden="true">
      <path fill="currentColor" d="M3 3h8v8H3V3zm2 2v4h4V5H5zm8-2h8v8h-8V3zm2 2v4h4V5h-4zM3 13h8v8H3v-8zm2 2v4h4v-4H5zm10-2h2v2h-2v-2zm2 2h2v2h-2v-2zm-4 2h2v2h-2v-2zm2 2h2v2h-2v-2zm4-2h2v4h-2v-4z" />
    </svg>
  );
}

function NavIcon({ name, active }) {
  const fill = active ? '#5716d7' : '#888888';
  if (name === 'home') {
    return (
      <svg width="22" height="22" viewBox="0 0 64 64" aria-hidden="true">
        {active ? (
          <path fill={fill} fillRule="evenodd" d="m23.361,4.95c5.073,-3.934 12.206,-3.934 17.279,0l16,12.408c3.379,2.621 5.36,6.635 5.36,10.893v19.905c0,7.67 -6.293,13.844 -14,13.844h-32c-7.707,0 -14,-6.173 -14,-13.844v-19.905c0,-4.259 1.981,-8.273 5.361,-10.893zM21,44c-1.105,0 -2,0.895 -2,2s0.895,2 2,2h22c1.105,0 2,-0.895 2,-2s-0.895,-2 -2,-2z" />
        ) : (
          <>
            <path fill={fill} fillRule="evenodd" d="m40.639,4.95c-5.073,-3.934 -12.206,-3.934 -17.279,0l-16,12.408c-3.379,2.621 -5.361,6.635 -5.361,10.893v19.905c0,7.67 6.293,13.844 14,13.844h32c7.707,0 14,-6.173 14,-13.844v-19.905c0,-4.259 -1.981,-8.273 -5.36,-10.893zM25.812,8.111c3.63,-2.815 8.746,-2.815 12.376,0l16,12.408c2.412,1.87 3.812,4.722 3.812,7.733v19.905c0,5.412 -4.452,9.844 -10,9.844h-32c-5.548,0 -10,-4.432 -10,-9.844v-19.905c0,-3.01 1.4,-5.862 3.812,-7.733z" />
            <path fill={fill} d="m19,46c0,-1.105 0.895,-2 2,-2h22c1.105,0 2,0.895 2,2s-0.895,2 -2,2h-22c-1.105,0 -2,-0.895 -2,-2z" />
          </>
        )}
      </svg>
    );
  }
  if (name === 'transactions') {
    const paths = active ? ANDROID_IC_TIPS_FILLED : ANDROID_IC_TIPS_OUTLINE;
    return (
      <svg width="22" height="22" viewBox="0 0 64 64" aria-hidden="true">
        {paths.map((d, i) => (
          <path key={i} fill={fill} fillRule="evenodd" d={d} />
        ))}
      </svg>
    );
  }
  if (name === 'analytics') {
    if (active) {
      return (
        <svg width="22" height="22" viewBox="0 0 64 64" aria-hidden="true">
          <path fill={fill} fillRule="evenodd" d={ANDROID_IC_REPORT_FILLED} />
        </svg>
      );
    }
    return (
      <svg width="22" height="22" viewBox="0 0 64 64" aria-hidden="true">
        <path fill={fill} fillRule="evenodd" d={ANDROID_IC_REPORT_OUTLINE[0]} />
        <path fill={fill} d={ANDROID_IC_REPORT_OUTLINE[1]} />
        <path fill={fill} d={ANDROID_IC_REPORT_OUTLINE[2]} />
        <path fill={fill} d={ANDROID_IC_REPORT_OUTLINE[3]} />
      </svg>
    );
  }
  return (
    <svg width="22" height="22" viewBox="0 0 64 64" aria-hidden="true">
      {active ? (
        <path fill={fill} fillRule="evenodd" d="m2.799,15.964c0.759,-8.058 7.665,-13.964 15.645,-13.964h27.086c7.94,0 14.83,5.848 15.625,13.866 1.128,11.387 1.126,20.742 -0.01,32.247 -0.792,8.025 -7.684,13.887 -15.634,13.887h-27.054c-7.987,0 -14.894,-5.915 -15.652,-13.978 -1.071,-11.396 -1.075,-20.707 -0.006,-32.058zM18,22c0,-3.866 3.134,-7 7,-7 3.171,0 5.85,2.109 6.71,5h12.29c1.105,0 2,0.895 2,2s-0.895,2 -2,2h-12.29c-0.86,2.891 -3.539,5 -6.71,5 -3.866,0 -7,-3.134 -7,-7zM39,35c3.866,0 7,3.134 7,7s-3.134,7 -7,7c-3.171,0 -5.85,-2.109 -6.71,-5h-12.29c-1.105,0 -2,-0.895 -2,-2s0.895,-2 2,-2h12.29c0.86,-2.891 3.539,-5 6.71,-5z" />
      ) : (
        <>
          <path fill={fill} fillRule="evenodd" d="m32.29,44c0.86,2.891 3.539,5 6.71,5 3.866,0 7,-3.134 7,-7s-3.134,-7 -7,-7c-3.171,0 -5.85,2.109 -6.71,5h-12.29c-1.105,0 -2,0.895 -2,2s0.895,2 2,2zM39,39c1.657,0 3,1.343 3,3s-1.343,3 -3,3 -3,-1.343 -3,-3 1.343,-3 3,-3z" />
          <path fill={fill} fillRule="evenodd" d="m2.799,15.964c0.759,-8.058 7.665,-13.964 15.645,-13.964h27.086c7.94,0 14.83,5.848 15.625,13.866 1.128,11.387 1.126,20.742 -0.01,32.247 -0.792,8.025 -7.684,13.887 -15.634,13.887h-27.054c-7.987,0 -14.894,-5.915 -15.652,-13.978 -1.071,-11.396 -1.075,-20.707 -0.006,-32.058zM18.445,6c-6.031,0 -11.108,4.447 -11.663,10.339 -1.045,11.1 -1.042,20.161 0.006,31.309 0.554,5.898 5.635,10.352 11.67,10.352h27.054c6.008,0 11.075,-4.415 11.654,-10.28 1.11,-11.246 1.112,-20.337 0.01,-31.46 -0.58,-5.856 -5.642,-10.26 -11.644,-10.26z" />
          <path fill={fill} fillRule="evenodd" d="m31.71,20c-0.86,-2.891 -3.539,-5 -6.71,-5 -3.866,0 -7,3.134 -7,7s3.134,7 7,7c3.171,0 5.85,-2.109 6.71,-5h12.29c1.105,0 2,-0.895 2,-2s-0.895,-2 -2,-2zM25,19c-1.657,0 -3,1.343 -3,3s1.343,3 3,3 3,-1.343 3,-3 -1.343,-3 -3,-3z" />
        </>
      )}
    </svg>
  );
}

/** @drawable/ic_info — matches employer QR dialog (dialog_qr_code.xml) */
function QrDialogInfoIcon() {
  return (
    <svg className="qr-dialog-info-icon" width="48" height="48" viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="currentColor"
        d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"
      />
    </svg>
  );
}

/** Layout aligned with Android `dialog_qr_code.xml` (TipPay Emp employer). */
function QrCodeModal({ open, loading, error, qrData, employerName, onClose }) {
  if (!open) return null;
  const qrLink = qrData?.qrCodeLink || '';
  const qrSrc = qrLink
    ? `https://api.qrserver.com/v1/create-qr-code/?size=280x280&data=${encodeURIComponent(qrLink)}`
    : '';
  const showQr = !loading && !error && qrSrc;

  return (
    <div
      className="qr-dialog-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="qr-dialog-title"
      onClick={onClose}
    >
      <div className="qr-dialog-card" onClick={(e) => e.stopPropagation()}>
        <p id="qr-dialog-title" className="qr-dialog-sr-only">
          Your tipping QR code
        </p>
        <button type="button" className="qr-dialog-close" onClick={onClose} aria-label="Close">
          <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
            <path
              fill="currentColor"
              d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"
            />
          </svg>
        </button>

        <div className="qr-dialog-logo">
          <img
            src={`${process.env.PUBLIC_URL}/logo192.png`}
            alt="TipPay"
            className="qr-dialog-logo-img"
            width={192}
            height={192}
          />
        </div>

        <div className="qr-dialog-user-strip">
          <span className="qr-dialog-user-label">User Name :</span>
          <span className="qr-dialog-user-name">{employerName}</span>
        </div>

        <div className="qr-dialog-qr-wrap">
          {loading ? (
            <div className="qr-dialog-qr qr-dialog-qr--loading">
              <div className="qr-dialog-spinner" />
            </div>
          ) : null}

          {!loading && error ? (
            <div className="qr-dialog-qr qr-dialog-qr--error">
              <div className="qr-dialog-qr-blur" />
              <div className="qr-dialog-profile-msg">
                <QrDialogInfoIcon />
                <p className="qr-dialog-profile-title">
                  Complete your profile to generate your QR code
                </p>
                <p className="qr-dialog-profile-sub">{error}</p>
              </div>
            </div>
          ) : null}

          {showQr ? (
            <div className="qr-dialog-qr qr-dialog-qr--ok">
              <img src={qrSrc} alt="QR code for tips" className="qr-dialog-qr-img" />
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function readEmployerDisplayName() {
  try {
    const raw = localStorage.getItem('user');
    const u = raw ? JSON.parse(raw) : null;
    return u?.name || u?.user?.name || u?.employer?.name || u?.profile?.name || 'Employer';
  } catch {
    return 'Employer';
  }
}

function ProtectedLayout() {
  const location = useLocation();
  const token = localStorage.getItem('token');
  const userRaw = localStorage.getItem('user');
  let user = null;
  try {
    user = userRaw ? JSON.parse(userRaw) : null;
  } catch (error) {
    user = null;
  }
  const role = (user?.role || user?.user?.role || '').toLowerCase();

  const [qrOpen, setQrOpen] = React.useState(false);
  const [qrLoading, setQrLoading] = React.useState(false);
  const [qrError, setQrError] = React.useState('');
  const [qrData, setQrData] = React.useState(null);

  if (!token || role !== 'employer') {
    return <Navigate to="/login" replace />;
  }

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    window.location.href = '/login';
  };

  const openQrModal = async () => {
    setQrOpen(true);
    setQrLoading(true);
    setQrError('');
    try {
      const data = await webApi.getDashboardQr();
      if (!data?.qrCodeLink) {
        throw new Error('QR code is unavailable. Please complete your profile setup.');
      }
      setQrData(data);
    } catch (error) {
      setQrError(error.message || 'Failed to load QR code');
      setQrData(null);
    } finally {
      setQrLoading(false);
    }
  };

  /* Single mobile-width column at all breakpoints (see --app-column-max in App.css) */
  const contentClass = 'app-content';

  return (
    <div className="app-shell">
      <main className={contentClass}>
        <Routes>
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/tips" element={<TipsPage />} />
          <Route path="/reviews" element={<ReviewsPage />} />
          <Route path="/more" element={<MorePage onLogout={logout} />} />
          <Route path="/employees/add" element={<AddEmployeePage />} />
          <Route path="/employees/:employeeId" element={<EmployeeDetailPage />} />
          <Route path="/employees" element={<EmployeesPage />} />
          <Route path="/distribution" element={<PoolDistributionPage />} />
          <Route path="/transactions" element={<TransactionsPage />} />
          <Route path="/transactions/all" element={<TransactionsAllPage />} />
          <Route path="/transactions/report" element={<TransactionReportPage />} />
          <Route path="/report" element={<ReportPage />} />
          <Route path="/payout" element={<PayoutPage />} />
          <Route path="/payout-history" element={<PayoutHistoryPage />} />
          <Route path="/shop-info" element={<ShopInfoPage />} />
          <Route path="/tip-mode" element={<TipModePage />} />
          <Route path="/bank-accounts" element={<BankAccountsPage />} />
          <Route path="/gst-verification" element={<GstVerificationPage />} />
          <Route path="/notifications" element={<NotificationsPage />} />
          <Route path="/change-password" element={<ChangePasswordPage />} />
          <Route path="/contact-us" element={<ContactUsPage />} />
        </Routes>
      </main>

      {location.pathname !== '/bank-accounts' &&
      location.pathname !== '/employees/add' &&
      location.pathname !== '/payout-history' &&
      location.pathname !== '/notifications' ? (
        <button type="button" className="dashboard-fab" onClick={openQrModal} aria-label="Scan QR Code">
          <QrIcon />
        </button>
      ) : null}
      <nav className="mobile-bottom-nav" aria-label="Primary navigation">
        {navItems.map((item) => (
          item.isPlaceholder ? (
            <span key="placeholder" className="mobile-nav-placeholder" aria-hidden="true" />
          ) : (
            <NavLink key={item.to} to={item.to}>
              {({ isActive }) => {
                const navActive =
                  item.to === '/transactions'
                    ? location.pathname.startsWith('/transactions')
                    : item.to === '/report'
                      ? location.pathname === '/report' || location.pathname === '/reviews'
                      : isActive;
                return (
                  <span className="mobile-nav-item">
                    <NavIcon name={item.icon} active={navActive} />
                    <span>{item.label}</span>
                  </span>
                );
              }}
            </NavLink>
          )
        ))}
      </nav>
      <QrCodeModal
        open={qrOpen}
        loading={qrLoading}
        error={qrError}
        qrData={qrData}
        employerName={readEmployerDisplayName()}
        onClose={() => setQrOpen(false)}
      />
    </div>
  );
}

function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/*" element={<ProtectedLayout />} />
    </Routes>
  );
}

export default App;
